import React, { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/ui-components/ui/dialog";
import { Sparkles, ArrowRight } from "lucide-react";
import { PlanKey, PLAN_NAMES } from '@/lib/api/interfaces/billing.interface';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

interface UpgradePlanDialogProps {
  /**
   * The trigger element that opens the dialog (optional)
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open (controlled mode)
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Required plan for the feature
   */
  requiredPlan: PlanKey;
  /**
   * The current plan of the user
   */
  currentPlan?: string;
  /**
   * Callback when the user clicks the upgrade button
   */
  onUpgrade?: () => void;
}

/**
 * Dialog to show when a user tries to access a feature their current plan doesn't allow.
 */
export const UpgradePlanDialog: React.FC<UpgradePlanDialogProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  requiredPlan,
  currentPlan,
  onUpgrade,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermissions();

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const requiredPlanName = PLAN_NAMES[requiredPlan] || requiredPlan;
  const currentPlanName = currentPlan ? (PLAN_NAMES[currentPlan] || currentPlan) : null;
  
  const hasNoPlan = !currentPlan || (requiredPlan === PlanKey.BASIC);
  const canManageBilling = can(Permission.MANAGE_BILLING);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setOpen(false);
      navigate('/vereine/billing');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      
      <DialogContent className="w-[calc(100%-2rem)] overflow-hidden rounded-3xl sm:rounded-3xl border border-gray-100 p-0 shadow-xl sm:w-full sm:max-w-md bg-white">
        <div className="p-8 text-center flex flex-col items-center pb-6">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <Sparkles className="h-7 w-7" />
          </div>
          
          <h2 className="mb-3 text-2xl font-bold tracking-tight text-gray-900">
            {hasNoPlan ? "Abo erforderlich" : "Upgrade auf " + requiredPlanName}
          </h2>
          <p className="text-sm leading-relaxed text-gray-500">
            {hasNoPlan 
              ? "Um dieses Feature zu nutzen, benötigst du einen aktiven Vertrag." 
              : "Dieses Feature ist in deinem aktuellen Plan nicht enthalten."}
          </p>
        </div>

        <div className="px-8 pb-8 space-y-6">
          <div className="w-full rounded-xl border border-gray-100 bg-gray-50 p-4 flex flex-col gap-3">
            {!hasNoPlan && currentPlanName && (
              <div className="flex justify-between items-center px-1">
                <span className="text-sm font-medium text-gray-500">Aktueller Plan</span>
                <span className="text-sm font-semibold text-gray-700">{currentPlanName}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <span className="text-sm font-medium text-gray-900">
                {hasNoPlan ? "Erforderlicher Vertrag" : "Erforderlicher Plan"}
              </span>
              <span className="text-sm font-bold text-blue-600">{requiredPlanName}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2">
            {canManageBilling && (
              <Button
                type="button"
                onClick={handleUpgrade}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all"
              >
                {hasNoPlan ? "Vertrag auswählen" : "Jetzt upgraden"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant={canManageBilling ? "outline" : "default"}
              onClick={() => setOpen(false)}
              className={`w-full h-11 rounded-xl transition-all ${
                canManageBilling 
                  ? "text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900" 
                  : "bg-gray-900 hover:bg-gray-800 text-white"
              }`}
            >
              {canManageBilling ? "Vielleicht später" : "Verstanden"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
